// Developer: Ankit Gupta
/** Feedback, the quarterly pulse and Product Insights (backend app/api/v1/feedback.py). */
import { api } from '@/api/client';

export type Kind = 'IDEA' | 'PROBLEM' | 'PRAISE' | 'QUESTION';
export type Status = 'NEW' | 'REVIEWING' | 'PLANNED' | 'SHIPPED' | 'DECLINED';
export type Area =
  | 'HOME' | 'ORION' | 'RHINO' | 'MIGRATION' | 'CONFY' | 'INSIGHT' | 'DATA_SYNC' | 'REPORTS' | 'COMPANY' | 'ADMIN' | 'OTHER';

/** In the order the drawer offers them; only praise carries the faces (stars). */
export const KINDS: Kind[] = ['PRAISE', 'PROBLEM', 'QUESTION', 'IDEA'];
export const STATUSES: Status[] = ['NEW', 'REVIEWING', 'PLANNED', 'SHIPPED', 'DECLINED'];
export const AREAS: Area[] = ['HOME', 'ORION', 'RHINO', 'MIGRATION', 'CONFY', 'INSIGHT', 'DATA_SYNC', 'REPORTS', 'COMPANY', 'ADMIN', 'OTHER'];

export const STATUS_COLOR: Record<Status, string> = {
  NEW: 'default', REVIEWING: 'processing', PLANNED: 'purple', SHIPPED: 'success', DECLINED: 'warning',
};
/** The five faces of the rating, unhappiest first; rating n is FACES[n - 1] and is worth n stars. */
export const FACES = ['😞', '🙁', '😐', '🙂', '😄'];

export const KIND_COLOR: Record<Kind, string> = { IDEA: 'gold', PROBLEM: 'red', PRAISE: 'green', QUESTION: 'blue' };

export interface Reply {
  id: number;
  author_name: string | null;
  author_role: 'TEAM' | 'COMPANY' | 'SENDER';
  body: string;
  created_at: string;
}

export interface Item {
  id: number;
  kind: Kind;
  rating: number | null;
  area: Area;
  page: string | null;
  application: string | null;
  body: string;
  contact_ok: boolean;
  has_screenshot: boolean;
  status: Status;
  status_changed_at: string | null;
  created_at: string;
  replies: Reply[];
  // for the people who triage
  user_email?: string | null;
  user_name?: string | null;
  organization_id?: number | null;
  organization_name?: string | null;
  tags?: string[];
  duplicate_of?: number | null;
  demand?: number;
}

export interface FeedbackIn {
  kind: Kind;
  rating: number | null;
  area: Area;
  page: string;
  application: string | null;
  body: string;
  language: string;
  contact_ok: boolean;
  screenshot: string | null;
}

export interface Ranked { id: number; body: string; area: Area; status: Status; demand: number; created_at: string }

export interface Insights {
  days: number;
  since: string;
  organization_id: number | null;
  organization_name: string | null;
  organizations?: { id: number; name: string }[];
  feedback: {
    total: number;
    people: number;
    rating: number | null;
    rated: number;
    stars: Record<'1' | '2' | '3' | '4' | '5', number>;
    by_kind: Record<Kind, number>;
    by_status: Record<Status, number>;
    trend: { week: string; count: number; rating: number | null }[];
  };
  areas: { area: Area; count: number; rating: number | null; problems: number; ideas: number; praise: number }[];
  usage: Record<'ORION' | 'RHINO' | 'MIGRATION' | 'CONFY', { actions: number; people: number }>;
  orion_applications: { application: string; actions: number; people: number }[];
  answers: { orion: { up: number; down: number }; rhino: { up: number; down: number } };
  pulse: {
    quarter: string;
    score: number | null;
    responses: number;
    dismissed: number;
    promoters: number;
    passives: number;
    detractors: number;
    history: { quarter: string; score: number | null; responses: number }[];
  };
  top_ideas: Ranked[];
  top_problems: Ranked[];
  shipped: { id: number; body: string; area: Area; shipped_at: string }[];
}

export const feedbackApi = {
  send: (body: FeedbackIn) => api.post<Item>('/feedback', body),
  mine: () => api.get<Item[]>('/feedback/mine'),
  reply: (id: number, body: string) => api.post<Item>(`/feedback/${id}/replies`, { body }),
  manage: (params: { status?: string; kind?: string; area?: string; organization_id?: number; q?: string }) =>
    api.get<Item[]>('/feedback/manage', params),
  triage: (id: number, body: { status?: Status; tags?: string[]; duplicate_of?: number; unmerge?: boolean }) =>
    api.put<Item>(`/feedback/${id}`, body),
  screenshotUrl: (id: number) => `/feedback/${id}/screenshot`,
  pulse: () => api.get<{ ask: boolean; quarter: string }>('/feedback/pulse'),
  answerPulse: (score: number | null, comment?: string) => api.post<{ ask: boolean }>('/feedback/pulse', { score, comment }),
  insights: (days: number, organization_id?: number) => api.get<Insights>('/feedback/insights', { days, organization_id }),
};

/** The part of the product a page belongs to, so the drawer can say where feedback came from. */
export function areaOf(pathname: string): Area {
  const p = pathname.toLowerCase();
  if (p === '/' || p.startsWith('/home')) return 'HOME';
  if (p.startsWith('/orion') || p.startsWith('/chat')) return 'ORION';
  if (p.startsWith('/rhino')) return 'RHINO';
  if (p.startsWith('/confy')) return 'CONFY';
  if (p.startsWith('/insight')) return 'INSIGHT';
  if (p.startsWith('/data-sync')) return 'DATA_SYNC';
  if (p.startsWith('/reports')) return 'REPORTS';
  if (p.startsWith('/company') || p.startsWith('/credits')) return 'COMPANY';
  if (p.startsWith('/manage')) return 'ADMIN';
  if (['/projects', '/runs', '/reconciliation', '/entity-library', '/fusion-extracts', '/issues', '/scheduled'].some((x) => p.startsWith(x))) return 'MIGRATION';
  return 'OTHER';
}
